import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, Check } from "lucide-react";
import ErrorMessage from "./ErrorMessage";

interface Option {
    value: string;
    label: string;
}

interface SearchSelectProps {
    label?: string;
    error?: string;
    placeholder?: string;
    options: Option[];
    value?: string;
    onChange: (value: string) => void;
    className?: string;
    disabled?: boolean;
    required?: boolean;
}

const SearchSelect: React.FC<SearchSelectProps> = ({
    label,
    error,
    placeholder = "Select option...",
    options = [],
    value,
    onChange,
    className,
    disabled = false,
    required = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm("");
        }
    }, [isOpen]);

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedOption = options.find(opt => opt.value === value);

    const handleSelect = (val: string) => {
        onChange(val);
        setIsOpen(false);
        setSearchTerm("");
    };

    return (
        <div className={`relative ${className || ""}`} ref={containerRef}>
            {label && (
                <label className="inline-block text-sm font-medium mb-2 text-gray-700">
                    {label}
                    {required && <span className="text-destructive ml-0.5">*</span>}
                </label>
            )}

            <div
                onClick={toggleDropdown}
                className={`flex h-9 w-full items-center justify-between rounded-lg border border-input bg-white px-3 py-2 text-sm shadow-sm transition-colors cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50"
                    } ${isOpen ? "ring-1 ring-primary border-primary" : ""}`}
            >
                <span className={!selectedOption ? "text-muted-foreground" : ""}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg shadow-black/10 animate-in fade-in-0 zoom-in-95">
                    <div className="flex items-center border-b px-3 py-2">
                        <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                        <input
                            className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="Search..."
                            autoFocus
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-gray-200">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    onClick={() => handleSelect(option.value)}
                                    className={`relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground ${option.value === value ? "bg-accent text-accent-foreground" : ""
                                        }`}
                                >
                                    <span className="flex-1">{option.label}</span>
                                    {option.value === value && (
                                        <Check className="h-4 w-4 text-primary" />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                                No results found.
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ErrorMessage errors={error} />
        </div>
    );
};

export default SearchSelect;